import React, { useEffect } from 'react';
import loadable from '@loadable/component';
import { useLocation } from 'react-router-dom';

import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { camelize } from '../../util/string';
import { propTypes } from '../../util/types';

import FallbackPage from '../LandingPage/FallbackPage';
import { ASSET_NAME } from './AboutUsPage.duck';
import Hero from '../../components/Cobe/Hero/Hero';
import Banner from '../../components/Cobe/Banner/Banner';
import Faq from '../../components/Cobe/Faq/Faq';
import OurMision from '../../components/Cobe/OurMission/OurMision';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

export const AboutUsPageComponent = props => {
  const { pageAssetsData, inProgress, error } = props;
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) return;

    const id = location.hash.slice(1); // 'guarantee'

    // Try immediately and then a little later in case content is still streaming in
    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.setAttribute('tabindex', '-1');
        el.focus({ preventScroll: true });
        return true;
      }
      return false;
    };

    if (!tryScroll()) {
      const raf = requestAnimationFrame(() => tryScroll());
      const t = setTimeout(() => tryScroll(), 250);
      return () => {
        cancelAnimationFrame(raf);
        clearTimeout(t);
      };
    }
  }, [location.hash, pageAssetsData, inProgress]);

  return (
    <>
      <PageBuilder
        pageAssetsData={pageAssetsData?.[camelize(ASSET_NAME)]?.data}
        inProgress={inProgress}
        error={error}
        fallbackPage={<FallbackPage error={error} />}
        topbarStartDark
      >
        <Hero isAboutPage />
        <Banner textId="Banner.Nr1Platform" />
        <OurMision />
        <Faq />
      </PageBuilder>
    </>
  );
};

AboutUsPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
  error: propTypes.error,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  const { currentUser } = state.user;

  return {
    pageAssetsData,
    inProgress,
    error,
    currentUser,
  };
};

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const AboutUsPage = compose(connect(mapStateToProps))(AboutUsPageComponent);

export default AboutUsPage;
